import { useMemo, useState } from 'react';
import { findNikkeInCatalog } from '../../nikke-catalog/domain/nikkeCatalog.js';
import { useNikkeCatalog } from '../../nikke-catalog/hooks/useNikkeCatalog.js';
import { NikkePortraitImage } from '../../nikke-catalog/shared/NikkePortraitImage.jsx';
import {
  DEFAULT_PRACTICE_TABLE_SORT,
  PRACTICE_TABLE_ELEMENTS,
  buildPracticeTableRows,
  filterPracticeTableRows,
  nextPracticeTableSort,
  sortPracticeTableRows,
} from '../domain/raidPracticeRecordTable.js';

const SORT_COLUMNS = [
  { key: 'nickname', label: '닉네임' },
  { key: 'boss', label: '보스' },
  { key: 'combo', label: '조합' },
  { key: 'damage', label: '딜량' },
  { key: 'peak', label: '고점%' },
];
const NO_ROWS = [];

function formatDamage(value) {
  return `${Number(value || 0).toLocaleString('ko-KR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}억`;
}

function formatPeak(value) {
  return value === null ? '·' : `${value.toFixed(2)}%`;
}

function SortButton({ column, sort, onSort }) {
  const active = sort.key === column.key;
  return (
    <button
      type="button"
      className={active ? 'active' : ''}
      aria-pressed={active}
      title={active ? '한 번 더 누르면 순서를 뒤집습니다' : `${column.label} 순으로 정렬`}
      onClick={() => onSort(column.key)}
    >
      {column.label}
      {active ? <i aria-hidden="true">{sort.direction === 'desc' ? '▾' : '▴'}</i> : null}
    </button>
  );
}

// The table's filter and sort state. The board owns it so the element buttons, 초기화 and the row count
// sit in the board's own toolbar row next to the search box; rows are only built while the table shows.
export function usePracticeRecordTable({ season, query = '', enabled = true }) {
  const [element, setElement] = useState('');
  const [sort, setSort] = useState(DEFAULT_PRACTICE_TABLE_SORT);
  const rows = useMemo(() => (enabled ? buildPracticeTableRows(season || {}) : NO_ROWS), [enabled, season]);
  const visible = useMemo(
    () => (enabled ? sortPracticeTableRows(filterPracticeTableRows(rows, { element, query }), sort) : NO_ROWS),
    [element, enabled, query, rows, sort],
  );
  return {
    rows,
    visible,
    element,
    setElement,
    sort,
    onSort: (key) => setSort((current) => nextPracticeTableSort(current, key)),
    reset: () => {
      setElement('');
      setSort(DEFAULT_PRACTICE_TABLE_SORT);
    },
  };
}

export function PracticeTableElementFilter({ element = '', onChange = () => {}, mobile = false }) {
  return (
    <div className="practice-record-table-elements" role="group" aria-label="보스 속성">
      <button type="button" className={!element ? 'active' : ''} aria-pressed={!element} onClick={() => onChange('')}>{mobile ? '전체' : '전체 보기'}</button>
      {PRACTICE_TABLE_ELEMENTS.map((item) => (
        <button
          type="button"
          key={item}
          data-element={item}
          className={element === item ? 'active' : ''}
          aria-pressed={element === item}
          onClick={() => onChange(element === item ? '' : item)}
        >
          {item}
        </button>
      ))}
    </div>
  );
}

// 전체 모의전 표 보기: every registered 모의전 slot on one row. 고점% is the row's damage against the
// same member's best on that boss.
export function RaidPracticeRecordTable({ rows = NO_ROWS, sort = DEFAULT_PRACTICE_TABLE_SORT, onSort = () => {}, mobile = false, canEdit = () => false, onOpen = () => {} }) {
  const { catalog } = useNikkeCatalog();
  const byColumn = Object.fromEntries(SORT_COLUMNS.map((column) => [column.key, column]));

  return (
    <section className={`practice-record-table ${mobile ? 'mobile' : 'desktop'}`} aria-label="모의전 표">
      <table className="practice-record-table-grid">
        <thead>
          <tr>
            <th scope="col"><SortButton column={byColumn.nickname} sort={sort} onSort={onSort} /></th>
            <th scope="col"><SortButton column={byColumn.boss} sort={sort} onSort={onSort} /></th>
            <th scope="col"><SortButton column={byColumn.combo} sort={sort} onSort={onSort} /></th>
            <th scope="col"><SortButton column={byColumn.damage} sort={sort} onSort={onSort} /></th>
            <th scope="col"><SortButton column={byColumn.peak} sort={sort} onSort={onSort} /></th>
            <th scope="col">제목</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => {
            const editable = canEdit(row);
            return (
              <tr
                key={row.id}
                className={editable ? 'is-editable' : ''}
                tabIndex={editable ? 0 : undefined}
                onClick={editable ? () => onOpen(row) : undefined}
                onKeyDown={editable ? (event) => { if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); onOpen(row); } } : undefined}
              >
                <td className="nickname"><span>{row.nickname}</span><small>S{row.slotNo || '-'}</small></td>
                <td className="boss" data-element={row.element || undefined}>{row.bossName}</td>
                <td className="deck">
                  <span>
                    {row.deck.map((code, index) => {
                      const item = findNikkeInCatalog(catalog, code);
                      return (
                        <span className="practice-record-table-portrait" key={`${code}-${index}`} title={item?.name || code}>
                          <NikkePortraitImage src={item?.imageUrl} alt={item?.name || code} fallbackText={item?.name?.slice(0, 1) || '?'} fallbackClassName="practice-record-table-portrait-fallback" />
                        </span>
                      );
                    })}
                  </span>
                </td>
                <td className="damage">{formatDamage(row.damage)}</td>
                <td className="peak">{formatPeak(row.peakPercent)}</td>
                <td className="title">{row.title}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
      {!rows.length ? <div className="practice-record-table-empty"><strong>조건에 맞는 모의전이 없습니다.</strong></div> : null}
    </section>
  );
}
